import React, { useState } from 'react';

const BookingForm = () => {
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [checkIn, setCheckIn] = useState('');
    const [checkOut, setCheckOut] = useState('');
    const [guests, setGuests] = useState(1);
    const [submitted, setSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const response = await fetch('/api/bookings.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name,
                    email, 
                    phone,
                    checkIn,
                    checkOut,
                    adults: guests, 
                    children: 0, 
                    bookingDate: new Date().toISOString(),
                  }),
            });
            if (response.ok) {
                setSubmitted(true);
                setName('');
                setCheckIn('');
                setCheckOut('');
                setGuests(1);
            } else {
                alert('There was a problem with your booking. Please try again.');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('There was a problem with your booking. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
            <h3 className="text-2xl font-bold text-blue-900 mb-6">Book Your Stay</h3>

            {submitted ? (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                    <p className="font-bold">Booking Request Received!</p>
                    <p>We'll contact you at {phone} to confirm your booking. Our staff will reach out to you shortly.</p>
                    <div className="mt-4 flex flex-col space-y-2">
                        <p className="font-semibold">Call us directly:</p>
                        <a href="tel:+919400949078" className="text-blue-600 hover:underline">9400949078</a>
                        <a href="tel:+919446907814" className="text-blue-600 hover:underline">9446907814</a>
                    </div>
                    <button
                        onClick={() => setSubmitted(false)}
                        className="mt-4 bg-blue-800 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                        Book Another Stay
                    </button>
                </div>
            ) : (
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label htmlFor="name" className="block text-gray-700 font-medium mb-2">Your Name</label>
                        <input
                            type="text"
                            id="name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                    </div>

                    <div className="mb-4">
                        <label htmlFor="email" className="block text-gray-700 font-medium mb-2">Email Address</label>
                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                    </div>

                    <div className="mb-4">
                        <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">Phone Number</label>
                        <input
                            type="tel"
                            id="phone"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                            pattern="[0-9]{10}"
                            title="Please enter a valid 10-digit phone number"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                            <label htmlFor="checkIn" className="block text-gray-700 font-medium mb-2">Check-in Date</label>
                            <input
                                type="date"
                                id="checkIn"
                                value={checkIn}
                                onChange={(e) => setCheckIn(e.target.value)}
                                min={new Date().toISOString().split('T')[0]}
                                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="checkOut" className="block text-gray-700 font-medium mb-2">Check-out Date</label>
                            <input
                                type="date"
                                id="checkOut"
                                value={checkOut}
                                onChange={(e) => setCheckOut(e.target.value)}
                                min={checkIn || new Date().toISOString().split('T')[0]}
                                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                            />
                        </div>
                    </div>

                    <div className="mb-6">
                        <label htmlFor="guests" className="block text-gray-700 font-medium mb-2">Number of Guests</label>
                        <select
                            id="guests"
                            value={guests}
                            onChange={(e) => setGuests(parseInt(e.target.value))}
                            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        >
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                                <option key={num} value={num}>{num} {num === 1 ? 'guest' : 'guests'}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex flex-col space-y-4">
                        <button
                            type="submit"
                            className="bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-bold py-3 px-4 rounded-lg transition-colors duration-200 flex justify-center items-center"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : null}
                            Book Now - No Payment Required
                        </button>

                        <div className="text-sm text-gray-600 text-center">
                            <span className="font-medium">Pay only when you arrive</span> - No advance payment needed
                        </div>
                    </div>
                </form>
            )}
        </div>
    );
};

export default BookingForm;